<script setup lang="ts">
import { ref } from 'vue';
import { useFlowStore } from '@/stores/flow';
import ErrorBanner from './ErrorBanner.vue';

const flow = useFlowStore();
const code = ref<string>(flow.code);

async function submit() {
  if (!code.value.trim()) return;
  await flow.applyCode(code.value);
}

function onInput(event: Event) {
  // Normalise to upper-case for the visual feedback most voucher
  // codes are issued in. Doesn't affect what the backend matches.
  const target = event.target as HTMLInputElement;
  code.value = target.value.toUpperCase();
}
</script>

<template>
  <section class="screen">
    <h1 class="title">Redeem a voucher</h1>
    <p class="lede">
      Enter the code from your invitation. We'll find the course it unlocks and
      get you ready to take it.
    </p>

    <ErrorBanner
      v-if="flow.error"
      :message="flow.error"
      @dismiss="flow.clearError()"
    />

    <form @submit.prevent="submit">
      <label class="field">
        <span class="label-text">Voucher code</span>
        <input
          :value="code"
          autocomplete="off"
          autocapitalize="characters"
          spellcheck="false"
          class="code-input"
          placeholder="e.g. LIME-7Q4-COCO"
          :disabled="flow.busy"
          @input="onInput"
        />
      </label>

      <button
        type="submit"
        class="btn btn-primary btn-block"
        :disabled="flow.busy || !code.trim()"
      >
        <span v-if="flow.busy" class="spinner" aria-hidden="true"></span>
        <span v-else>Continue</span>
      </button>
    </form>

    <p class="hint">
      Don't have a code? <a href="/" class="link">Back to the home page</a>.
    </p>
  </section>
</template>

<style scoped>
.screen { display: flex; flex-direction: column; }

.title {
  font-family: 'Fraunces', Georgia, serif;
  font-size: clamp(1.6rem, 3vw, 2.1rem);
  letter-spacing: -0.02em;
  color: var(--coco-900);
  margin: 0 0 0.6rem;
  font-weight: 500;
  font-variation-settings: 'SOFT' 50, 'opsz' 96;
}
.lede {
  margin: 0 0 1.5rem;
  color: var(--ink-soft);
  line-height: 1.55;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-bottom: 1rem;
}
.label-text {
  font-size: 0.88rem;
  font-weight: 500;
  color: var(--ink);
}
.code-input {
  font: inherit;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 1.15rem;
  letter-spacing: 0.06em;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  border: 1.5px solid var(--coco-200);
  background: var(--cream);
  color: var(--coco-900);
  transition: border-color 160ms var(--ease), box-shadow 160ms var(--ease);
}
.code-input:focus {
  outline: none;
  border-color: var(--lime-500);
  box-shadow: 0 0 0 4px var(--lime-100);
}
.code-input::placeholder {
  color: var(--ink-mute);
  letter-spacing: 0.04em;
}

.hint {
  margin: 1.25rem 0 0;
  font-size: 0.88rem;
  color: var(--ink-mute);
}
.link { color: var(--lime-700); }
.link:hover { color: var(--lime-800); }
</style>
